<template>
  <div class="py-4 container-fluid">
    <div class="card">
      <div class="card-header pb-0">
        <div class="d-flex align-items-center mb-3">
          <h6 class="mb-0">Shopify — Órdenes</h6>
          <argon-button
            color="primary"
            size="sm"
            class="ms-auto"
            :disabled="isSyncing"
            @click="syncOrders"
          >
            <span v-if="isSyncing">
              <i class="fas fa-spinner fa-spin me-1"></i>
              Sincronizando...
            </span>
            <span v-else>Sincronizar</span>
          </argon-button>
        </div>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="searchQuery" class="mb-1">Buscar</label>
            <input
              id="searchQuery"
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="Buscar por orden, cliente, email o ID..."
              @input="fetchOrders(1)"
            />
          </div>
          <div class="col-md-3 mb-3">
            <label for="statusFilter" class="mb-1">Estado</label>
            <select
              id="statusFilter"
              v-model="statusFilter"
              class="form-select"
              @change="fetchOrders(1)"
            >
              <option value="">Todos</option>
              <option
                v-for="status in statusOptions"
                :key="status"
                :value="status"
              >
                {{ status }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="card-body px-0 pt-0 pb-2">
        <div v-if="isLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-1"></i>
          Cargando órdenes...
        </div>

        <p v-else-if="!orders.length" class="text-muted px-4 mb-0">
          No hay órdenes. Pulsa Sincronizar para traerlas desde Shopify o registra ventas desde el POS.
        </p>

        <div v-else class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4" style="width: 56px">
                  &nbsp;
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Orden
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Cliente
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Pago
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Envío
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Estado
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Items
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Total
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Fecha
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in orders" :key="`${order.source}-${order.id}`">
                <td class="text-center ps-4">
                  <i
                    v-if="isOrderSynced(order)"
                    class="fas fa-check-circle text-success"
                    title="Sincronizado con Shopify"
                  ></i>
                  <button
                    v-else-if="canSyncOrder(order)"
                    type="button"
                    class="btn btn-link btn-sm p-0 text-secondary"
                    :disabled="syncingOrderId === order.id"
                    title="Enviar pedido a Shopify"
                    @click="syncPosOrder(order)"
                  >
                    <i
                      class="fas"
                      :class="syncingOrderId === order.id ? 'fa-spinner fa-spin' : 'fa-sync'"
                    ></i>
                  </button>
                </td>
                <td>
                  <span class="text-sm font-weight-bold d-block">{{ orderLabel(order) }}</span>
                  <span class="text-xs text-secondary">{{ orderSubtitle(order) }}</span>
                </td>
                <td>
                  <span class="text-sm font-weight-bold d-block">{{ orderCustomerName(order) }}</span>
                  <span v-if="orderCustomerEmail(order)" class="text-xs text-secondary">{{ orderCustomerEmail(order) }}</span>
                </td>
                <td class="text-center">
                  <span class="badge badge-sm" :class="financialStatusClass(order.financial_status)">
                    {{ orderPaymentMethod(order) }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="badge badge-sm" :class="fulfillmentStatusClass(order.fulfillment_status)">
                    {{ orderFulfillmentLabel(order) }}
                  </span>
                </td>
                <td class="text-center">
                  <select
                    class="form-select form-select-sm order-status-select"
                    :value="currentOrderStatus(order)"
                    :disabled="updatingStatusKey === orderStatusKey(order)"
                    @change="updateOrderStatus(order, $event.target.value)"
                  >
                    <option
                      v-for="status in statusOptions"
                      :key="status"
                      :value="status"
                    >
                      {{ status }}
                    </option>
                  </select>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ orderLineItemsCount(order) }}</span>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ formatMoney(orderTotal(order), orderCurrency(order)) }}</span>
                </td>
                <td class="text-center">
                  <span class="text-xs text-secondary">{{ formatDate(orderDate(order)) }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="pagination p-3">
            <a
              v-for="link in links"
              :key="link.label"
              class="page-item cursor-pointer"
              :class="{ active: link.active, disabled: !link.url }"
              @click="link.url && fetchOrdersFromLink(link.url)"
            >
              {{ link.label }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import shopifyService from "@/services/shopify.service";
import Swal from "sweetalert2";

export default {
  name: "ShopifyOrders",
  components: {
    ArgonButton,
  },
  data() {
    return {
      isSyncing: false,
      isLoading: false,
      syncingOrderId: null,
      updatingStatusKey: null,
      statusOptions: [
        "NO PREPARADO",
        "PREPARADO",
        "DESPACHADO",
        "ENTREGADO",
      ],
      orders: [],
      links: [],
      searchQuery: "",
      statusFilter: "",
    };
  },
  mounted() {
    this.fetchOrders();
  },
  methods: {
    async fetchOrders(page = 1) {
      this.isLoading = true;
      try {
        const response = await shopifyService.getOrders({
          page,
          search: this.searchQuery,
          status: this.statusFilter,
          per_page: 15,
        });
        const data = response?.data || {};

        this.orders = data.data || [];
        this.links = data.links || [];
      } catch (error) {
        console.error("Error al cargar órdenes Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || "No se pudieron cargar las órdenes.",
          "error"
        );
      } finally {
        this.isLoading = false;
      }
    },
    fetchOrdersFromLink(url) {
      const page = new URL(url).searchParams.get("page") || 1;
      this.fetchOrders(Number(page));
    },
    async syncOrders() {
      this.isSyncing = true;
      try {
        const response = await shopifyService.syncOrders();
        const data = response?.data || {};

        if (data.success) {
          const sync = data.sync || {};
          await this.fetchOrders(1);
          await Swal.fire(
            "Sincronizado",
            `Se procesaron ${sync.total ?? 0} órdenes (${sync.created ?? 0} nuevas, ${sync.updated ?? 0} actualizadas).`,
            "success"
          );
        } else {
          throw new Error(data.message || "No se pudo sincronizar.");
        }
      } catch (error) {
        console.error("Error al sincronizar órdenes Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al sincronizar órdenes con Shopify.",
          "error"
        );
      } finally {
        this.isSyncing = false;
      }
    },
    isOrderSynced(order) {
      if (order?.source === "shopify" || order?.shopify_order_id) {
        return true;
      }

      return order?.shopify_product_id != null;
    },
    canSyncOrder(order) {
      return order?.source === "pos" && !this.isOrderSynced(order);
    },
    async syncPosOrder(order) {
      if (!this.canSyncOrder(order)) {
        return;
      }

      const confirm = await Swal.fire({
        title: "Enviar a Shopify",
        text: `¿Deseas crear el pedido #${order.id} en Shopify?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, sincronizar",
        cancelButtonText: "Cancelar",
      });

      if (!confirm.isConfirmed) {
        return;
      }

      this.syncingOrderId = order.id;

      try {
        const response = await shopifyService.syncPosSale(order.id);
        const data = response?.data || {};

        if (!data.success) {
          throw new Error(data.message || "No se pudo sincronizar la venta.");
        }

        await Swal.fire(
          "Sincronizado",
          `Pedido creado en Shopify: ${data.shopify_order_name || data.shopify_order_id}`,
          "success"
        );

        await this.fetchOrders();
      } catch (error) {
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "No se pudo enviar el pedido a Shopify.",
          "error"
        );
      } finally {
        this.syncingOrderId = null;
      }
    },
    orderLabel(order) {
      if (order?.source === "shopify") {
        return order.title || `#${order.shopify_order_id}`;
      }

      return `#${order.id}`;
    },
    orderSubtitle(order) {
      if (order?.source === "shopify") {
        return `ID ${order.shopify_order_id}`;
      }

      if (order?.shopify_product_id) {
        return `Shopify #${order.shopify_product_id}`;
      }

      return order.title || "Venta POS";
    },
    orderFulfillmentLabel(order) {
      if (order?.source === "pos") {
        return this.orderDeliveryLabel(order);
      }

      return order?.fulfillment_status || "—";
    },
    orderDeliveryLabel(order) {
      const method = this.orderVariants(order).delivery_method || "";

      return (
        {
          envio_nacional: "ENVIO NACIONAL",
          envio_local: "ENVIO LOCAL",
          efectivo: "EFECTIVO",
          retiro: "RETIRO EN TIENDA",
          domicilio: "DOMICILIO",
        }[method] || method.toUpperCase() || "POS"
      );
    },
    orderStatusKey(order) {
      return `${order.source}-${order.id}`;
    },
    currentOrderStatus(order) {
      const history = Array.isArray(order?.status_history) ? order.status_history : [];

      if (!history.length) {
        return "NO PREPARADO";
      }

      return history[history.length - 1]?.status || "NO PREPARADO";
    },
    async updateOrderStatus(order, status) {
      if (!order?.source || !order?.id || status === this.currentOrderStatus(order)) {
        return;
      }

      const key = this.orderStatusKey(order);
      const source = order.source;
      const id = order.id;
      this.updatingStatusKey = key;

      try {
        const response = await shopifyService.updateOrderStatus(source, id, status);
        const data = response?.data || {};

        if (!data.success) {
          throw new Error(data.message || "No se pudo actualizar el estado.");
        }

        if (data.status_history) {
          const orderIndex = this.orders.findIndex(
            (item) => item.source === source && String(item.id) === String(id)
          );

          if (orderIndex !== -1) {
            this.orders.splice(orderIndex, 1, {
              ...this.orders[orderIndex],
              status_history: data.status_history,
            });
          }
        }
      } catch (error) {
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "No se pudo actualizar el estado del pedido.",
          "error"
        );
        await this.fetchOrders();
      } finally {
        this.updatingStatusKey = null;
      }
    },
    orderVariants(order) {
      return order?.variants || {};
    },
    orderCustomerName(order) {
      return this.orderVariants(order).customer_name || order?.customer_name || "—";
    },
    orderCustomerEmail(order) {
      return this.orderVariants(order).customer_email || order?.email || "";
    },
    orderPaymentMethod(order) {
      const method = this.orderVariants(order).payment_method || order?.financial_status || "—";
      return String(method).toUpperCase();
    },
    orderLineItemsCount(order) {
      const items = this.orderVariants(order).line_items;
      if (Array.isArray(items)) return items.length;
      return order?.line_items_count ?? order?.variants_count ?? 0;
    },
    orderTotal(order) {
      return this.orderVariants(order).total ?? order?.total_price ?? order?.price_max;
    },
    orderDate(order) {
      return this.orderVariants(order).created_at || order?.synced_at || order?.shopify_created_at;
    },
    financialStatusClass(status) {
      if (status === "paid") return "bg-gradient-success";
      if (status === "pending" || status === "partially_paid") return "bg-gradient-warning";
      if (status === "refunded" || status === "voided") return "bg-gradient-secondary";
      return "bg-gradient-info";
    },
    fulfillmentStatusClass(status) {
      if (status === "pos") return "bg-gradient-secondary";
      if (status === "fulfilled") return "bg-gradient-success";
      if (status === "partial") return "bg-gradient-warning";
      if (!status) return "bg-gradient-secondary";
      return "bg-gradient-info";
    },
    formatMoney(value, currency = "COP") {
      if (value == null) return "—";

      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: currency || "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },
    orderCurrency(order) {
      return order?.currency || "COP";
    },
    formatDate(value) {
      if (!value) return "—";

      return new Date(value).toLocaleString("es-CO", {
        dateStyle: "short",
        timeStyle: "short",
      });
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.pagination {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
}

.page-item {
  margin: 0 5px;
  padding: 4px 8px;
  user-select: none;
}

.page-item.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.active {
  font-weight: bold;
}

.order-status-select {
  min-width: 140px;
  font-size: 0.75rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}
</style>
